import { SearchState } from "react-instantsearch-core";
import {
  useState,
  ComponentType,
  ReactElement,
  useCallback,
  useEffect,
} from "react";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { t } from "@lingui/macro";
import { I18n } from "@lingui/core";
import {
  mapValues,
  isArray,
  map,
  isEqual,
  find,
  difference,
  filter,
  startsWith,
  keys,
  includes,
  union,
} from "lodash-es";
import { filterObject, strToArray } from "src/modules/brands/utils";
import { AlgoliaFacet, HitType } from "src/types/Algolia";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import {
  SIZE_FACET,
  BRAND_FACET,
  COLOUR_FACET,
  AGES_FACET,
  PATTERN_FACET,
  STYLE_FACET,
  PROMOTION_FACET,
  GENDERS_FACET,
  PRICE_FACET,
  CATEGORIES_FACET,
  CONSCIOUS_FACET,
  BRAND_DEPARTMENTS_FACET,
  LABELS_TYPE_FACET,
  PROMOTION_ID_FACET,
  IS_CLEARANCE_PRODUCT_FACET,
  BRAND_DESIGNER_ONLY_FACET,
  COLLECTIONS_FACET,
  CATEGORIES_SLUG_FACET,
  BRAND_IS_PRIVATE_SALE,
  IS_DELETED,
  DISCOUNT_FACET,
  BRAND_SLUG_FACET,
} from "src/utils/algolia";
import {
  createCategoryTree,
  getItemsToRefine,
} from "./utils/createCategoryTree";
import { Department } from "src/types/department";
import { PLPType } from "src/types/PLPType.d";
import { CategoryTreeSlugOrder } from "src/organisms/AlgoliaProductList/AlgoliaProductList";
import { RankingInfo } from "src/contexts/app.context";

interface Props {
  searchState: any;
  brandName?: string;
  brandSlug?: string;
  brandId?: string;
  collectionId?: string;
  genderId: string;
  slug?: string;
  locale: string;
  createURL: (state: any) => string;
  onSearchStateChange: () => void;
  categorySlug?: string;
  setLoading?: (value: boolean) => void;
  department?: Department;
  plpType: PLPType;
  initialFilters?: string[];
  categoryTreeSortOrder?: CategoryTreeSlugOrder;
}

export enum OptionsSortBy {
  popular = "popular",
  new = "_publish_date_desc",
  "new-discount" = "_discount_date_desc",
  price = "_price_asc",
  "price-desc" = "_price_desc",
}

enum OptionsSortByBackward {
  popular = "popular",
  "_publish_date_desc" = "new",
  "_discount_date_desc" = "new-discount",
  "_price_asc" = "price",
  "_price_desc" = "price-desc",
}

export const getSortByOptions = (
  i18n: I18n
): { [key: string]: string | number }[] => [
  {
    key: 1,
    text: t(i18n)`Popularity`,
    value: "popular",
  },
  { key: 2, text: t(i18n)`New`, value: OptionsSortBy.new },
  {
    key: 3,
    text: t(i18n)`New discount`,
    value: OptionsSortBy["new-discount"],
  },
  {
    key: 4,
    text: t(i18n)`Price: high to low`,
    value: OptionsSortBy["price-desc"],
  },
  {
    key: 5,
    text: t(i18n)`Price: low to high`,
    value: OptionsSortBy.price,
  },
];

const idsFromMeta = (metas: string[]) =>
  metas.map((meta) => meta.split("||")[0]);

const slugsFromMeta = (metas: string[]) =>
  metas.map((meta) => meta.split("||")[2]);

const urlToSearchState = ({
  gender,
  facets,
  routerQuery,
  plpType,
  brandName,
  brandId,
  categorySlug,
  department,
  genderId,
  categoryTreeSortOrder,
}: {
  gender: string;
  genderId: string;
  facets: AlgoliaFacet[];
  routerQuery: ParsedUrlQuery;
  plpType: PLPType;
  brandName?: string;
  brandId?: string;
  categorySlug?: string;
  department?: Department;
  categoryTreeSortOrder?: CategoryTreeSlugOrder;
}): SearchState & {
  selectedCatSlug?: string[];
} => {
  const {
    cat_slug,
    brands,
    brand_slug,
    label,
    max_price,
    min_price,
    sizes,
    styles,
    patterns,
    ages,
    colors,
    orderBy,
    goodOnYouScore,
    discount,
  } = routerQuery;

  const searchState = initialSearchState({
    gender,
    routerQuery,
    brandName,
    brandId,
    categorySlug,
    department,
  });

  if (!searchState.refinementList) {
    return searchState;
  }

  if (!searchState.menu) {
    searchState.menu = {};
  }

  if (label) {
    const facet = getFacetById(facets, PROMOTION_FACET, label);
    if (facet && facet.length) {
      searchState.menu[PROMOTION_FACET] = facet[0];
    }
  }

  if (discount) {
    const discountFacet = getFacetById(facets, DISCOUNT_FACET, discount);
    if (discountFacet && discountFacet.length) {
      searchState.menu[DISCOUNT_FACET] = discountFacet[0];
    }
  }

  if (min_price !== undefined && searchState.range) {
    if (!searchState.range.price) {
      searchState.range.price = {} as { min: number; max: number };
    }
    searchState.range.price.min = Number(min_price);
  }
  if (max_price !== undefined && searchState.range) {
    if (!searchState.range.price) {
      searchState.range.price = {} as { min: number; max: number };
    }
    searchState.range.price.max = Number(max_price);
  }
  if (sizes) {
    searchState.refinementList[SIZE_FACET] = getFacetById(
      facets,
      SIZE_FACET,
      sizes
    );
  }
  if (styles) {
    searchState.refinementList[STYLE_FACET] = getFacetById(
      facets,
      STYLE_FACET,
      styles
    );
  }
  if (patterns) {
    searchState.refinementList[PATTERN_FACET] = getFacetById(
      facets,
      PATTERN_FACET,
      patterns
    );
  }
  if (ages) {
    searchState.refinementList[AGES_FACET] = getFacetById(
      facets,
      AGES_FACET,
      ages
    );
  }
  if (colors) {
    searchState.refinementList[COLOUR_FACET] = getFacetById(
      facets,
      COLOUR_FACET,
      colors
    );
  }
  if (brands) {
    searchState.refinementList[BRAND_FACET] = getFacetById(
      facets,
      BRAND_FACET,
      brands
    );
  }
  if (brand_slug && !brands) {
    searchState.refinementList[BRAND_FACET] = getFacetByProp(
      facets,
      "slug",
      BRAND_FACET,
      brand_slug
    );
  }
  if (goodOnYouScore) {
    searchState.refinementList[CONSCIOUS_FACET] = getFacetById(
      facets,
      CONSCIOUS_FACET,
      goodOnYouScore
    );
  }
  if (orderBy) {
    searchState.sortBy = OptionsSortBy[orderBy as "popular"];
  }
  if (cat_slug) {
    const categoryFacets = facets.find((e) => e.name === CATEGORIES_FACET);
    const { flatLabelsMap } = createCategoryTree(
      keys(categoryFacets?.data).map((e) => ({
        label: e,
      })) as unknown as HitType[],
      gender
    );
    const selectedCatSlug = (isArray(cat_slug) ? cat_slug : [cat_slug])
      .map((e) => flatLabelsMap[e])
      .filter((e) => e);
    const toRefine = getItemsToRefine(selectedCatSlug);
    searchState.selectedCatSlug = selectedCatSlug;
    searchState.refinementList[CATEGORIES_FACET] = toRefine;
  }

  if (
    categorySlug ||
    department ||
    includes([PLPType.NEW_IN, PLPType.SEARCH_PLP], plpType)
  ) {
    const categoryFacets = facets.find((e) => e.name === CATEGORIES_FACET);
    const { tree } = createCategoryTree(
      keys(categoryFacets?.data).map((e) => ({
        label: e,
      })) as unknown as HitType[],
      gender,
      categoryTreeSortOrder
    );
    searchState.categoriesTree = tree;
  }

  if (categorySlug && categorySlug !== genderId) {
    const categoryFilter = getFilterBySlug(
      facets,
      CATEGORIES_FACET,
      categorySlug
    );
    if (categoryFilter) {
      searchState.keepSelectedIgnoreInActive = {
        [CATEGORIES_FACET]: [categoryFilter],
      };

      if (searchState.refinementList[CATEGORIES_FACET]) {
        if (
          !includes(
            searchState.refinementList[CATEGORIES_FACET],
            categoryFilter
          )
        ) {
          const toRefine = [
            ...(searchState.selectedCatSlug || []),
            categoryFilter,
          ];
          searchState.refinementList[CATEGORIES_FACET] =
            getItemsToRefine(toRefine);
        }
      } else {
        searchState.refinementList[CATEGORIES_FACET] = [categoryFilter];
      }
    } else {
      searchState.missingCategorySlug = true;
    }
  }
  return searchState;
};

const stateToArrays = (metas?: string[]) => {
  if (metas) {
    return {
      ids: idsFromMeta(metas),
      slugs: slugsFromMeta(metas),
    };
  }
  return undefined;
};

const getFacetByProp = (
  facets: AlgoliaFacet[],
  propName: string,
  filterName: string,
  items: string | string[]
): string[] => {
  items = isArray(items) ? items : [items];
  const facet = facets.find((e) => e.name === filterName);
  if (!facet) {
    return [];
  }
  const metas = Object.keys(facet.data);

  return items
    .map((item) => {
      if (propName === "id") {
        return metas.find((meta) => meta.split("||")[0] === item);
      }
      return metas.find((meta) => meta.split("||")[2] === item);
    })
    .filter((e) => !!e) as string[];
};

const getFacetById = (
  facets: AlgoliaFacet[],
  name: string,
  ids: string | string[]
): string[] => {
  ids = isArray(ids) ? ids : [ids];
  const facet = facets.find((e) => e.name === name);
  if (!facet) {
    return [];
  }
  const metas = Object.keys(facet.data);
  return map(ids, (id) =>
    metas.find((meta) => meta.split("||")[0] === id)
  ).filter((e) => !!e) as string[];
};

const getFilterBySlug = (
  facets: AlgoliaFacet[],
  name: string,
  slug?: string
): string | undefined => {
  const facet = facets.find((e) => e.name === name);
  if (!facet || !slug) {
    return;
  }
  return find(Object.keys(facet.data), (meta) => meta.split("||")[2] === slug);
};

const searchStateToURL = (
  searchState: SearchState & { selectedCatSlug?: string[] },
  isBrand: boolean
) => {
  const promotion = searchState.menu?.[PROMOTION_FACET]?.length
    ? searchState.menu?.[PROMOTION_FACET].split("||")[0]
    : undefined;
  const discount = searchState.menu?.[DISCOUNT_FACET]?.length
    ? searchState.menu?.[DISCOUNT_FACET].split("||")[0]
    : undefined;
  const routeState = {
    page:
      searchState.page && searchState.page > 1 ? searchState.page : undefined,
    min_price: searchState.range?.price?.min,
    max_price: searchState.range?.price?.max,
    sizes: stateToArrays(searchState.refinementList?.[SIZE_FACET])?.ids,
    label: promotion && promotion !== "ALL" ? promotion : undefined,
    discount: discount || undefined,
    colors: stateToArrays(searchState.refinementList?.[COLOUR_FACET])?.ids,
    styles: stateToArrays(searchState.refinementList?.[STYLE_FACET])?.ids,
    patterns: stateToArrays(searchState.refinementList?.[PATTERN_FACET])?.ids,
    ages: stateToArrays(searchState.refinementList?.[AGES_FACET])?.ids,
    brands: isBrand
      ? undefined
      : stateToArrays(searchState.refinementList?.[BRAND_FACET])?.ids,
    brand_slug: isBrand
      ? undefined
      : stateToArrays(searchState.refinementList?.[BRAND_FACET])?.slugs,
    orderBy:
      searchState.sortBy &&
      OptionsSortByBackward[searchState.sortBy as "_price_desc"],
    cat_slug:
      searchState.selectedCatSlug && searchState.selectedCatSlug.length
        ? searchState.selectedCatSlug.map((label) => label.split("||")[2])
        : undefined,
    goodOnYouScore: stateToArrays(searchState.refinementList?.[CONSCIOUS_FACET])
      ?.ids,
    q: searchState.query,
  };

  return filterObject(routeState);
};

const initialSearchState = ({
  routerQuery,
}: {
  gender: string;
  routerQuery: ParsedUrlQuery;
  brandName?: string;
  brandId?: string;
  categorySlug?: string;
  department?: Department;
}): SearchState & {
  selectedCatSlug?: string[];
  missingCategorySlug: boolean;
} => {
  const searchState = {
    refinementList: {},
    range: {},
    hitsPerPage: 72,
    missingCategorySlug: false,
    query: routerQuery.q,
  } as SearchState & {
    refinementList: { [key: string]: string[] };
    missingCategorySlug: boolean;
  };

  const { page } = routerQuery;

  searchState.page = page ? Number(page) : 1;

  return searchState;
};

function withAlgoliaState<P>(Component: ComponentType<P>) {
  // eslint-disable-next-line react/display-name
  return (props: Props): ReactElement<P & Props> => {
    const {
      brandName,
      brandId,
      brandSlug,
      collectionId,
      genderId,
      categorySlug,
      setLoading,
      department,
      plpType,
      categoryTreeSortOrder,
    } = props;
    const gender = localeFix({ gender: genderId });

    const router = useRouter();
    const [searchState, setSearchState] = useState<SearchState>(
      initialSearchState({
        gender,
        routerQuery: router.query,
        brandName,
        brandId,
        categorySlug,
        department,
      })
    );
    const [initialLoad, setInitialLoad] = useState(false);
    const [algoliaFacets, setAlgoliaFacets] = useState<AlgoliaFacet[] | null>(
      null
    );
    const [defaultPriceRange, setDefaultPriceRange] = useState({
      min: 0,
      max: 0,
    });
    const [statePayload, setStatePayload] = useState<
      (SearchState & { selectedCatSlug: string[] }) | null
    >(null);

    const [rankingInfo, setRankingInfo] = useState<RankingInfo>({});
    const [rankingInfoChanged, setRankingInfoChanged] =
      useState<boolean>(false);

    // TODO: use from the hook https://otrium.atlassian.net/browse/SHOP-2330
    const initialFilters = [
      `${GENDERS_FACET}:${gender}`,
      `${BRAND_IS_PRIVATE_SALE}:false`,
      `${IS_DELETED}:false`,
    ];

    if (plpType === PLPType.ICONS_PLP) {
      initialFilters.push(`${LABELS_TYPE_FACET}:icons`);
    }
    if (plpType === PLPType.NEW_IN) {
      initialFilters.push(`${LABELS_TYPE_FACET}:new`);
    }
    if (plpType === PLPType.HOT_DEALS) {
      initialFilters.push(`${PROMOTION_ID_FACET}:HOT_DEALS`);
    }
    if (plpType === PLPType.CLEARANCE_PLP) {
      initialFilters.push(`${IS_CLEARANCE_PRODUCT_FACET}:true`);
    }
    if (plpType === PLPType.DESIGNER_PLP && department) {
      initialFilters.push(`${BRAND_DEPARTMENTS_FACET}:"${department.value}"`);
    }
    if (brandSlug) {
      initialFilters.push(`${BRAND_SLUG_FACET}:${brandSlug}`);
    }
    if (plpType === PLPType.COLLECTIONS_PLP && collectionId) {
      initialFilters.push(`${COLLECTIONS_FACET}:${collectionId}`);
    }

    if (
      [
        PLPType.CATEGORY_PLP,
        PLPType.NEW_IN,
        PLPType.HOT_DEALS,
        PLPType.CLEARANCE_PLP,
      ].includes(plpType)
    ) {
      initialFilters.push(`${BRAND_DESIGNER_ONLY_FACET}:false`);
    }
    if (
      plpType === PLPType.CATEGORY_PLP &&
      categorySlug &&
      searchState.missingCategorySlug
    ) {
      initialFilters.push(`${CATEGORIES_SLUG_FACET}:${categorySlug}`);
    }

    const syncWithURL = useCallback(
      (nextState: SearchState) => {
        const {
          genderId: genderQuery,
          categories,
          category,
          brandOrCategoriesSlug: brandSlug,
          label,
          discount,
          max_price,
          min_price,
          cat_slug,
          sizes,
          styles,
          patterns,
          ages,
          page,
          colors,
          orderBy,
          slug: routerSlug,
          goodOnYouScore,
          brands,
          brand_slug,
          collectionSlug,
          q,
          ...rest
        } = router.query;
        const [asPathname] = router.asPath.split("?");
        const stateToUrl = searchStateToURL(
          nextState,
          !!router.query.brandSlug
        );

        const updatedRouterQuery = {
          ...(nextState.query && {
            q: nextState.query,
          }),
          ...rest,
          ...stateToUrl,
        };

        void router.push(
          {
            pathname: router.pathname,
            query: updatedRouterQuery,
          },
          {
            pathname: asPathname,
            query: updatedRouterQuery,
          },
          { shallow: true }
        );
      },
      [brandSlug, categorySlug] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const syncCategoryTree = (
      prevState: SearchState & {
        selectedCatSlug?: string[];
      },
      nextState: SearchState & {
        selectedCatSlug?: string[];
      }
    ) => {
      let prevCat = prevState.refinementList?.[CATEGORIES_FACET] || [];
      let nextCat = nextState.refinementList?.[CATEGORIES_FACET] || [];
      if (!nextCat.length) {
        nextState.selectedCatSlug = [];
        return;
      }
      if (JSON.stringify(prevCat) === JSON.stringify(nextCat)) {
        return;
      }
      prevCat = isArray(prevCat)
        ? prevCat
        : (prevCat as string).length
        ? [prevCat]
        : [];
      nextCat = isArray(nextCat)
        ? nextCat
        : (nextCat as string).length
        ? [nextCat]
        : [];
      const wasRefined = difference(prevCat, nextCat);
      if (
        wasRefined[0] &&
        nextState.refinementList?.[CATEGORIES_FACET] !== undefined
      ) {
        const newSelectedCatoCat = filter(
          nextState.selectedCatSlug || [],
          (item) => !startsWith(item, wasRefined[0])
        ).filter((e) => e);
        const toRefine = getItemsToRefine(newSelectedCatoCat);
        nextState.selectedCatSlug = newSelectedCatoCat;
        nextState.refinementList[CATEGORIES_FACET] = toRefine;
      }
    };

    const syncKeepSelected = (
      oldSearchState: SearchState & {
        keepSelectedIgnoreInActive?: { [p: string]: string[] };
      },
      nextSearchState: SearchState & {
        keepSelectedIgnoreInActive?: { [p: string]: string[] };
      }
    ) => {
      if (!oldSearchState.keepSelectedIgnoreInActive) {
        return;
      }
      nextSearchState.keepSelectedIgnoreInActive =
        oldSearchState.keepSelectedIgnoreInActive;
      if (!nextSearchState.refinementList) {
        nextSearchState.refinementList = {};
      }
      if (!nextSearchState.refinementList[CATEGORIES_FACET]) {
        nextSearchState.refinementList[CATEGORIES_FACET] =
          oldSearchState.keepSelectedIgnoreInActive[CATEGORIES_FACET].slice();
      } else {
        const toRefine = union(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          nextSearchState.selectedCatSlug,
          oldSearchState.keepSelectedIgnoreInActive[CATEGORIES_FACET]
        );
        nextSearchState.refinementList[CATEGORIES_FACET] =
          getItemsToRefine(toRefine);
      }
    };

    const onSearchStateChange = (
      nextSearchState: SearchState,
      shouldNotSyncCatoCat?: boolean
    ) => {
      if (JSON.stringify(searchState) !== JSON.stringify(nextSearchState)) {
        syncKeepSelected(searchState, nextSearchState);
        if (!shouldNotSyncCatoCat) {
          syncCategoryTree(searchState, nextSearchState);
        }
        setSearchState(nextSearchState);
        if (
          searchState.query !== nextSearchState.query ||
          searchState.page !== nextSearchState.page
        ) {
          syncWithURL(nextSearchState);
        }
      }
    };

    const setSearchStateFromFacets = (facets: AlgoliaFacet[]) => {
      const nextSearchState = urlToSearchState({
        gender,
        facets,
        routerQuery: router.query,
        plpType,
        brandName,
        brandId,
        categorySlug,
        department,
        genderId,
        categoryTreeSortOrder,
      });
      setSearchState(nextSearchState);
    };

    const onInitialLoad = (
      facets: AlgoliaFacet[],
      rankingInfo: RankingInfo,
      indexName?: string
    ) => {
      if (!initialLoad) {
        setAlgoliaFacets(facets);
        setSearchStateFromFacets(facets);
        setInitialLoad(true);
        setRankingInfo(rankingInfo);
      }
      if (
        indexName === rankingInfo.indexUsed &&
        !rankingInfoChanged &&
        initialLoad
      ) {
        setRankingInfoChanged(true);
        setRankingInfo(rankingInfo);
      }
    };

    const onResultsChanged = useCallback(
      (facets: AlgoliaFacet[], rankingInfo: RankingInfo) => {
        const priceFacet = find(facets, { name: PRICE_FACET }) as
          | { stats: { min: number; max: number } }
          | undefined;
        if (!priceFacet) {
          return;
        }
        if (
          defaultPriceRange.min !== priceFacet?.stats.min ||
          defaultPriceRange.max !== priceFacet?.stats.max
        ) {
          setDefaultPriceRange({
            min: priceFacet?.stats.min || 0,
            max: priceFacet?.stats.max || 0,
          });
        }
      },
      [defaultPriceRange.min, defaultPriceRange.max]
    );

    useEffect(() => {
      if (initialLoad) {
        const {
          genderId: genderQuery,
          categories,
          brandOrCategoriesSlug: brandSlug,
          brands,
          brand_slug,
          ...rest
        } = router.query;
        const newURLQuery = mapValues(rest, (value) => strToArray(value));
        const currentSearchState = searchStateToURL(
          searchState,
          !!router.query.brandSlug
        );
        const isEqualState = isEqual(newURLQuery, currentSearchState);
        if (!isEqualState && algoliaFacets) {
          setSearchStateFromFacets(algoliaFacets);
        }
      }
      if (setLoading) {
        setLoading(!initialLoad);
      }
    }, [initialLoad, router.asPath]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (statePayload) {
        const nextSearchState = {
          ...searchState,
          ...statePayload,
          page: 1,
          refinementList: {
            ...searchState.refinementList,
            ...statePayload.refinementList,
          },
        };
        setStatePayload(null);
        onSearchStateChange(nextSearchState, true);
        syncWithURL(nextSearchState);
      }
    }, [statePayload]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (initialLoad && algoliaFacets) {
        setSearchStateFromFacets(algoliaFacets);
      }
    }, [algoliaFacets]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <Component
        {...(props as unknown as P)}
        searchState={searchState}
        createURL={searchStateToURL}
        onSearchStateChange={onSearchStateChange}
        syncWithURL={syncWithURL}
        onInitialLoad={onInitialLoad}
        onResultsChanged={onResultsChanged}
        defaultPriceRange={defaultPriceRange}
        setStatePayload={setStatePayload}
        initialFilters={initialFilters}
        rankingInfo={rankingInfo}
      />
    );
  };
}

export default withAlgoliaState;
